import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import JsonEditor from './components/JsonEditor';
import { formatLocalDateTime } from './timezone';

const containerStyle = {
  padding: '40px',
  maxWidth: '1000px',
  margin: '0 auto',
  width: '100%',
  color: 'var(--text-primary)',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  boxSizing: 'border-box'
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  background: 'var(--bg-secondary)',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: '0 4px 6px var(--shadow-color)'
};

const thStyle = {
  textAlign: 'left',
  padding: '12px 16px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-tertiary)',
  fontWeight: '600',
  color: 'var(--text-secondary)',
  fontSize: '14px'
};

const tdStyle = {
  padding: '12px 16px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '14px',
  color: 'var(--text-primary)'
};

const btnStyle = (bg, disabled = false) => ({
  padding: '8px 16px',
  background: disabled ? 'var(--bg-tertiary)' : bg,
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'background 0.2s',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px'
});

const badgeStyle = (type) => {
  let bg = 'var(--bg-tertiary)';
  let color = 'var(--text-secondary)';
  
  if (type === 'CREATE') { bg = 'rgba(40, 167, 69, 0.2)'; color = 'var(--success-color)'; }
  else if (type === 'UPDATE') { bg = 'rgba(23, 162, 184, 0.2)'; color = 'var(--info-color)'; }
  else if (type === 'DELETE') { bg = 'rgba(220, 53, 69, 0.2)'; color = 'var(--error-color)'; }
  else if (type === 'RUN') { bg = 'rgba(255, 193, 7, 0.2)'; color = 'var(--warning-color)'; }
  
  return {
    display: 'inline-block',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: bg,
    color: color
  };
};

export default function AuditLogViewer({ onBack }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(0);
  const [limit] = useState(50);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/audit-logs`, {
        params: { skip: page * limit, limit },
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(response.data);
    } catch (error) {
      console.error("Failed to fetch audit logs", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const formatDate = (dateString) => {
    return formatLocalDateTime(dateString);
  };

  const formatDetails = (details) => {
    if (!details) return 'No details';
    try {
      return JSON.stringify(JSON.parse(details), null, 2);
    } catch (e) {
      return details;
    }
  };

  const exportLogs = async (exportFormat) => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/audit-logs/export`, {
        params: { export_format: exportFormat },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const contentDisposition = response.headers['content-disposition'];
      let fileName = `audit_logs.${exportFormat}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/i);
        if (match && match[1]) {
          fileName = match[1];
        }
      }
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Failed to export audit logs", error);
      alert("Failed to export audit logs");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: '24px', cursor: 'pointer' }}>
            ←
          </button>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Audit Logs</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={() => exportLogs('json')}
            disabled={exporting || loading}
            style={btnStyle('var(--info-color)', exporting || loading)}
          >
            Export JSON
          </button>
          <button
            onClick={() => exportLogs('csv')}
            disabled={exporting || loading}
            style={btnStyle('var(--success-color)', exporting || loading)}
          >
            Export CSV
          </button>
          <button 
            onClick={() => setPage(Math.max(0, page - 1))} 
            disabled={page === 0 || loading}
            style={btnStyle('var(--button-bg)', page === 0 || loading)}
          >
            Previous
          </button>
          <span style={{ display: 'flex', alignItems: 'center', color: 'var(--text-secondary)' }}>
            Page {page + 1}
          </span>
          <button 
            onClick={() => setPage(page + 1)} 
            disabled={logs.length < limit || loading}
            style={btnStyle('var(--button-bg)', logs.length < limit || loading)}
          >
            Next
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', border: '1px solid var(--border-color)', borderRadius: '8px' }}>
        <table style={tableStyle}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
            <tr>
              <th style={thStyle}>Timestamp</th>
              <th style={thStyle}>Action</th>
              <th style={thStyle}>Entity</th>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>User</th>
              <th style={thStyle}>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                  Loading...
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-tertiary)' }}>
                  No logs found.
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <React.Fragment key={log.id}>
                  <tr style={{ background: expandedId === log.id ? 'var(--bg-tertiary)' : 'transparent', cursor: 'pointer' }} onClick={() => toggleExpand(log.id)}>
                    <td style={tdStyle}>{formatDate(log.timestamp)}</td>
                    <td style={tdStyle}>
                      <span style={badgeStyle(log.action)}>{log.action}</span>
                    </td>
                    <td style={tdStyle}>
                        {log.entity_type}
                        {log.entity_name && (
                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{log.entity_name}</div>
                        )}
                    </td>
                    <td style={tdStyle}>
                        <span title={log.entity_id} style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.1)', padding: '2px 4px', borderRadius: '4px' }}>
                            {log.entity_id.substring(0, 8)}...
                        </span>
                    </td>
                    <td style={tdStyle}>
                        {log.username ? (
                            <span title={`ID: ${log.user_id}`}>{log.username}</span>
                        ) : (
                            <span style={{ color: 'var(--text-tertiary)' }}>{log.user_id || 'System'}</span>
                        )}
                    </td>
                    <td style={tdStyle}>
                      <button 
                        style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '12px' }}
                      >
                        {expandedId === log.id ? 'Hide' : 'View'}
                      </button>
                    </td>
                  </tr>
                  {expandedId === log.id && (
                    <tr style={{ background: 'var(--bg-tertiary)' }}>
                      <td colSpan="6" style={{ padding: '0 20px 20px 20px', borderBottom: '1px solid var(--border-color)' }}>
                        <div style={{ background: 'var(--bg-primary)', padding: '15px', borderRadius: '4px', overflow: 'auto' }}>
                          <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '12px', color: 'var(--text-secondary)' }}>Full Details</h4>
                          <JsonEditor
                            value={log.details ? JSON.stringify(JSON.parse(log.details), null, 2) : 'No details'}
                            readOnly={true}
                            style={{ 
                              minHeight: '100px', 
                              border: 'none', 
                              background: 'transparent',
                              padding: 0
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
